import * as React from 'react';
import { type SvgIconProps } from '@mui/material/SvgIcon';
import {
  Navigate,
  Outlet,
  createFileRoute,
  Link,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import CssBaseline from '@mui/material/CssBaseline';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PolicyIcon from '@mui/icons-material/Policy';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import HighlightIcon from '@mui/icons-material/Highlight';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
// import NotificationsIcon from '@mui/icons-material/NotificationsNone';
// import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
// import SettingsIcon from '@mui/icons-material/Settings';
import ErrorIcon from '@mui/icons-material/Error';

import { useAppAuth } from '../hooks/use-app-auth';
import { MINIMUM_SEARCH_TERM_LENGTH } from '../utils/constants';
import {
  isOrgAdmin, isSuperAdmin, isSalesOnly, getSalesRouteAccess,
} from '../utils/roles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useApolloClient, useMutation, useQuery,
} from '@apollo/client/react';
import { useTourContext } from '../contexts/tour-context';
import { useTenantBranding } from '../contexts/tenant-branding-context';
import { NoTourModal } from '../components/no-tour-modal/no-tour-modal';
import { UpdateAvailableSnackbar } from '../components/update-available-snackbar/update-available-snackbar';
import { NotAuthorizedBox } from '../components/not-authorized-box/not-authorized-box';

import GET_GROUPS_BY_ORG_ID, {
  GroupsByOrgId,
  GroupsByOrgIdVariables,
} from '../graphql/queries/get-groups-by-org-id';
import CREATE_ACCOUNT, {
  CreateAccount,
  CreateAccountVariables,
} from '../graphql/mutations/create-account';
import SEARCH_ACCOUNTS_BY_COMPANY_NAME_AND_ORG_ID, {
  SearchAccountsByCompanyNameAndOrgId,
  SearchAccountsByCompanyNameAndOrgIdVariables,
} from '../graphql/queries/search-accounts-by-company-name-and-org-id';
import { AccountWithCompanyInfo } from '../graphql/types/accounts';
import SEARCH_COMPANIES, {
  SearchCompanies,
  SearchCompaniesVariables,
} from '../graphql/queries/search-companies';
import { CompanySearchResult } from '../graphql/types/companies';
import GraphQLMutationStatusMessage from '../components/graphql-mutation-status-message/graphql-mutation-status-message';
import theme from '../theme';
import useBreakPoint from '../hooks/use-break-point';
// import LanguageSelect from '../components/language-select/language-select';
import CompanySearchBar from '../components/company-search-bar/company-search-bar';
import DatadogUserTracking from '../components/datadog-user-tracking/datadog-user-tracking';
import OrganizationSwitchModal from '../components/organization-switch-modal/organization-switch-modal';
import BuildOverrideIndicator from '../components/build-override-indicator/build-override-indicator';
import SettingsMenu, { SettingsMenuItem } from '../components/settings-menu/settings-menu';
import { clearSessionStorage } from '../utils/storage-helpers';
import { getActiveBuildOverride } from '../utils/api-base-url';

type NavMenuItem = {
  text: string
  path: string
  icon: (props: SvgIconProps) => React.ReactNode
  selected?: boolean
}

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export const NAVBAR_HEIGHT = 64; // Material-UI's default toolbar height
const DRAWER_WIDTH = 260;

export const Route = createFileRoute('/_auth')({
  component: AuthLayout,
});

/*
In this file, we use:
useQuery - for the groups query because it's a one-time fetch when the component
mounts and we want automatic re-fetching if the user's org_id changes

client.query - for the search function because:
  We need manual control over when the query runs (when the user types)
  We need to handle cancellation of previous requests when new searches are made
  We want to make the queries in parallel using Promise.all
  We don't want the queries to automatically re-run when variables change
*/
function AuthLayout () {
  const { t } = useTranslation('routes');
  const [ open, setOpen ] = React.useState<boolean>(false);
  const [ isLoggingOut, setIsLoggingOut ] = React.useState<boolean>(false);
  const [ isLoading, setIsLoading ] = React.useState<boolean>(false);
  const [ orgSwitchModalOpen, setOrgSwitchModalOpen ] = React.useState<boolean>(false);
  const [ noTourModalOpen, setNoTourModalOpen ] = React.useState<boolean>(false);
  const { requestTour } = useTourContext();
  const { brand } = useTenantBranding();
  const [ internalResults, setInternalResults ] = React.useState<any[] | null>(null);
  const [ externalResults, setExternalResults ] = React.useState<any[] | null>(null);
  const [ internalTotalCount, setInternalTotalCount ] = React.useState<number>(0);
  const [ externalTotalCount, setExternalTotalCount ] = React.useState<number>(0);
  const client = useApolloClient();
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const latestSearchTermRef = React.useRef<string>('');

  // Use centralized auth hook
  const auth = useAppAuth();
  const {
    logout,
    isLoading: auth0Loading,
    user,
    error,
    loginWithRedirect,
  } = auth;
  const isScreenMedWidthOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation({
    select: (location) => location.pathname,
  });
  const { getBreakPointName } = useBreakPoint();
  const navigate = useNavigate();
  const salesOnly = isSalesOnly(user);

  // Add groups query
  const { data: groupsData } = useQuery<GroupsByOrgId, GroupsByOrgIdVariables>(
    GET_GROUPS_BY_ORG_ID,
    {
      variables: {
        orgId: user?.org_id || '',
      },
      skip: !user?.org_id || auth0Loading || salesOnly,
    },
  );

  const [ createAccount, { loading: isCreatingAccount, error: createAccountError } ] = useMutation<CreateAccount, CreateAccountVariables>(
    CREATE_ACCOUNT,
  );

  React.useEffect(() => {
    if (isScreenMedWidthOrSmaller) {
      setOpen(false);
    }
  }, [ isScreenMedWidthOrSmaller, location ]);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleOpenOrgSwitchModal = () => {
    setOrgSwitchModalOpen(true);
  };

  const handleCloseOrgSwitchModal = () => {
    setOrgSwitchModalOpen(false);
  };

  const pageHasTour =
    location === '/' ||
    location.startsWith('/accounts/') ||
    location === '/credit-applications' ||
    location.startsWith('/search');

  const handleTakeTour = () => {
    if (pageHasTour) {
      requestTour();
    } else {
      setNoTourModalOpen(true);
    }
  };

  const navItems: NavMenuItem[] = [
    ...(salesOnly
      ? []
      : [
          {
            text: t('home'),
            path: '/',
            icon: (props: SvgIconProps) => <HomeIcon {...props} />,
          },
          {
            text: t('advanced_search'),
            path: '/search',
            icon: (props: SvgIconProps) => <ManageSearchIcon {...props} />,
          },
        ]
    ),
    {
      text: t('credit_applications'),
      path: '/credit-applications',
      icon: (props) => <AssignmentIcon {...props} />,
    },
    ...(salesOnly
      ? []
      : [
          {
            text: t('reporting'),
            path: '/reporting/ar-aging',
            icon: (props: SvgIconProps) => <BarChartIcon {...props} />,
          },
          {
            text: t('bank_connections'),
            path: '/bank-connections',
            icon: (props: SvgIconProps) => <AccountBalanceIcon {...props} />,
          },
        ]
    ),
    // Visible to ORG_ADMIN and SUPER_ADMIN users
    ...((isOrgAdmin(user) || isSuperAdmin(user))
      ? [
          {
            text: t('users'),
            path: '/users',
            icon: (props: SvgIconProps) => <PeopleIcon {...props} />,
          },
        ]
      : []
    ),
    // Only show credit policies for SUPER_ADMIN users
    ...(isSuperAdmin(user)
      ? [
          {
            text: t('credit_policies'),
            path: '/credit-policies',
            icon: (props: SvgIconProps) => <PolicyIcon {...props} />,
          },
          {
            text: t('super_admin'),
            path: '/super-admin',
            icon: (props: SvgIconProps) => <AdminPanelSettingsIcon {...props} />,
          },
        ]
      : []
    ),
    // {
    //   text: t('notifications'),
    //   path: '/notifications',
    //   icon: (props) => <NotificationsIcon {...props} />,
    // },
    // {
    //   text: t('document_upload'),
    //   path: '/documents',
    //   icon: (props) => <ContentPasteSearchIcon {...props} />,
    // },
    // {
    //   text: t('admin_panel'),
    //   path: '/admin',
    //   icon: (props) => <SettingsIcon {...props} />,
    // },
  ];
  const settingsItems: SettingsMenuItem[] = [
    // {
    //   key: 'language_select',
    //   action: () => null,
    //   component: <LanguageSelect />,
    // },
    // Only show switch organization for SUPER_ADMIN users
    ...(isSuperAdmin(user)
      ? [
          {
            key: 'switch_organization',
            text: t('switch_organization'),
            icon: <SwapHorizIcon />,
            action: handleOpenOrgSwitchModal,
          },
        ]
      : []
    ),
    {
      key: 'take_tour',
      text: t('take_tour'),
      icon: <HighlightIcon />,
      action: handleTakeTour,
    },
    {
      key: 'logout',
      text: t('logout_btn'),
      icon: (isLoggingOut) ? <CircularProgress size={20} /> : <LogoutIcon />,
      action: async () => {
        setIsLoggingOut(true);
        await clearSessionStorage();
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      },
      // Keep the menu open during logout so the CircularProgress icon swap stays visible
      closesMenu: false,
    },
  ];

  const baseDrawerStyles = {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: (open) ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      width: 0,
    },
    [theme.breakpoints.up('md')]: {
      width: (open) ? DRAWER_WIDTH : theme.spacing(8),
    },
  };

  const handleSearch = async (searchTerm: string) => {
    // Clear results and counts immediately if search term is empty
    if (!searchTerm || searchTerm.length < MINIMUM_SEARCH_TERM_LENGTH || !user?.org_id) {
      setInternalResults(null);
      setExternalResults(null);
      setInternalTotalCount(0);
      setExternalTotalCount(0);
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    // Update the latest search term
    latestSearchTermRef.current = searchTerm;

    setIsLoading(true);
    setInternalResults(null);
    setExternalResults(null);
    setInternalTotalCount(0);
    setExternalTotalCount(0);

    try {
      // Make both API calls in parallel
      const [ accountsResponse, companiesResponse ] = await Promise.all([
        client.query<SearchAccountsByCompanyNameAndOrgId, SearchAccountsByCompanyNameAndOrgIdVariables>({
          query: SEARCH_ACCOUNTS_BY_COMPANY_NAME_AND_ORG_ID,
          variables: {
            name: searchTerm,
            orgId: user.org_id,
            skip: 0,
            limit: 10,
          },
          context: { signal: abortControllerRef.current.signal },
        }),
        client.query<SearchCompanies, SearchCompaniesVariables>({
          query: SEARCH_COMPANIES,
          variables: {
            name: searchTerm,
            page: 1,
            pageSize: 10,
          },
          context: { signal: abortControllerRef.current.signal },
        }),
      ]);

      // Only update state if this is still the most recent search
      if (latestSearchTermRef.current === searchTerm && !abortControllerRef.current.signal.aborted) {
        // Transform and update internal results
        const accountResults = accountsResponse?.data?.searchAccountsByCompanyNameAndOrgId.accounts.map((account: AccountWithCompanyInfo) => ({
          id: account.id,
          companyId: account.companyId,
          groupId: account.groupId,
          name: account.name,
          status: account.status,
          statusDescription: account.statusDescription,
          address: account.simpleAddress,
          type: account.officeType,
          externalId: account.externalId,
          source: 'internal' as const,
          erpId: account.erpId,
          crmId: account.crmId,
          alternateName: account.alternateName,
        }));

        const companyResults = companiesResponse?.data?.searchCompanies.companies
          .map((company: CompanySearchResult) => ({
            id: company.companySearchId,
            companyId: null,
            name: company.name,
            status: company.status,
            statusDescription: company.statusDescription,
            address: company.address,
            type: company.officeType,
            source: 'external' as const,
            erpId: null,
            crmId: null,
            alternateName: null,
          }));

        // Update all state at once to prevent partial updates
        setInternalResults(accountResults ?? null);
        setExternalResults(companyResults ?? null);
        setInternalTotalCount(accountsResponse?.data?.searchAccountsByCompanyNameAndOrgId.totalAccounts ?? 0);
        setExternalTotalCount(companiesResponse?.data?.searchCompanies.totalSize ?? 0);
      }
    } catch (error: unknown) {
      // Only log errors that aren't from cancellation
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
      // Only clear results if this is still the most recent search
      if (latestSearchTermRef.current === searchTerm && !abortControllerRef.current.signal.aborted) {
        setInternalResults(null);
        setExternalResults(null);
        setInternalTotalCount(0);
        setExternalTotalCount(0);
      }
    } finally {
      // Only update loading state if this is still the most recent search
      if (latestSearchTermRef.current === searchTerm && !abortControllerRef.current.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleViewCompany = (id: string) => {
    navigate({
      to: '/accounts/$accountId',
      params: { accountId: id },
    });
  };

  const handleAddCompany = async (id: string, groupId: string | null) => {
    if (!user?.org_id) {
      console.error('No organization ID available');
      return;
    }

    try {
      const { data } = await createAccount({
        variables: {
          orgId: user.org_id,
          groupId,
          companySearchId: id,
        },
      });

      if (data?.createAccount) {
        // Reset the data grid session storage so it goes back to default state
        sessionStorage.removeItem('com.creditpulse.app.data-grid-model');

        // Clear all cached data to force a complete refetch
        client.cache.evict({ fieldName: 'getAccountSummaries' });
        client.cache.evict({ fieldName: 'searchAccountsByCompanyNameAndOrgId' });
        client.cache.evict({ fieldName: 'getAccount' });
        client.cache.gc();

        // Navigate to the new account's page using the companySearchId
        navigate({
          to: '/accounts/$accountId',
          params: { accountId: data.createAccount },
        });
      } else {
        console.error('Failed to create account');
      }
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  const handleAdvancedSearchClick = (companyName: string) => {
    // Create search parameters with company name (if provided) and US country code
    const searchParams = new URLSearchParams();
    if (companyName && companyName.trim() !== '') {
      searchParams.set('companyName', companyName);
    }
    searchParams.set('country', 'US');

    navigate({
      to: `/search/${searchParams.toString()}`,
    });
  };

  // Stable for the session: surfaced in the sidebar so a developer can see at a glance that a
  // non-prod gql-api PR build is being targeted, including after a reload where the ?build=
  // param has already been stripped from the URL.
  const buildOverride = getActiveBuildOverride();

  const renderContent = () => {
    if (auth0Loading) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 4,
        }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (!salesOnly) {
      return <Outlet />;
    }
    switch (getSalesRouteAccess(location)) {
      case 'redirect':
        return (
          <Navigate
            replace
            to='/credit-applications'
          />
        );
      case 'allow':
        return <Outlet />;
      default:
        return <NotAuthorizedBox />;
    }
  };

  return (
    <>
      {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
      <CssBaseline />
      <DatadogUserTracking />
      <Backdrop
        open={isCreatingAccount}
        sx={{
          color: '#fff',
          zIndex: 2000,
        }}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <GraphQLMutationStatusMessage
        autoHide={true}
        message={isCreatingAccount ? t('create_account_pending_message') : createAccountError?.message || null}
        severity={isCreatingAccount ? 'info' : createAccountError ? 'error' : 'success'}
      />
      <Dialog
        aria-describedby='alert-dialog-description'
        aria-labelledby='alert-dialog-title'
        open={!!error}
      >
        <DialogTitle
          id='alert-dialog-title'
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ErrorIcon sx={{ mr: 1 }} />
          There was an error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <Typography>
              We were unable to authenticate you due to:
              {error?.message}
            </Typography>
            <Typography sx={{ mt: 2 }}>Click OK to log in again</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => loginWithRedirect()}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <MuiAppBar
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create([ 'margin', 'width' ], {
              easing: theme.transitions.easing.sharp,
              duration: (open) ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
            }),
            width: (open) ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
            marginLeft: (open) ? `${DRAWER_WIDTH}px` : '0px',
            boxShadow: 'none',
            borderWidth: 0,
            borderStyle: 'solid',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            borderBottomWidth: 'thin',
          }}
        >
          <Toolbar
            sx={{ height: NAVBAR_HEIGHT }}
          >
            <IconButton
              aria-label='open drawer'
              color='inherit'
              edge='start'
              sx={{
                marginRight: 0.5,
                [theme.breakpoints.up('md')]: {
                  marginRight: 2,
                },
                ...(open && { display: 'none' }),
              }}
              onClick={handleDrawerOpen}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                height: 35,
                [theme.breakpoints.up('md')]: {
                  height: 45,
                },
              }}
            >
              <img
                alt={t('logo_alt_text', { brandName: brand.brandName })}
                src={(getBreakPointName() === 'xs') ? brand.logoIconUrl : brand.logoUrl}
                style={{
                  verticalAlign: 'middle',
                  height: '100%',
                }}
              />
            </Box>
            <Box
              data-tour='search-bar'
              sx={{
                flexGrow: 1,
                mx: {
                  xs: 1,
                  sm: 2,
                  md: 4,
                },
              }}
            >
              {!salesOnly && (
                <CompanySearchBar
                  externalSearchResults={externalResults}
                  externalTotalCount={externalTotalCount}
                  groups={groupsData?.getGroupsByOrgId?.groups ?? null}
                  internalSearchResults={internalResults}
                  internalTotalCount={internalTotalCount}
                  isLoading={isLoading}
                  onAddCompany={handleAddCompany}
                  onAdvancedSearchClick={handleAdvancedSearchClick}
                  onSearch={handleSearch}
                  onViewCompany={handleViewCompany}
                />
              )}
            </Box>
            <Box sx={{
              flexShrink: 0,
              mr: 1.5,
            }}
            >
              <Tooltip title={t('support_tooltip')}>
                <IconButton
                  aria-label='support'
                  component='a'
                  href='https://support.creditpulse.com/'
                  rel='noopener noreferrer'
                  sx={{
                    p: 0,
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    minHeight: 40,
                    borderRadius: '50%',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(121, 106, 232, 0.08)',
                    },
                  }}
                  target='_blank'
                >
                  <QuestionMarkIcon sx={{ color: 'primary.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <SettingsMenu
              items={settingsItems}
              loading={auth0Loading}
              user={user}
            />
          </Toolbar>
        </MuiAppBar>
        <MuiDrawer
          sx={{
            ...baseDrawerStyles,
            '& .MuiDrawer-paper': {
              ...baseDrawerStyles,
              [theme.breakpoints.down('md')]: {
                width: (open) ? DRAWER_WIDTH : 0,
              },
            },
          }}
          variant='permanent'
        >
          <DrawerHeader>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List data-tour='sidebar-nav'>
            {navItems.map(({
              text,
              path,
              icon,
            }) => (
              <Link
                key={text}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
                to={path}
              >
                {({ isActive }) => (
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isActive}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: 2.5,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        {icon({ color: isActive ? 'primary' : undefined })}
                      </ListItemIcon>
                      <ListItemText
                        disableTypography={true}
                        primary={text}
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          opacity: open ? 1 : 0,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </Link>
            ))}
          </List>
          <Box sx={{ mt: 'auto' }}>
            <BuildOverrideIndicator
              open={open}
              override={buildOverride}
            />
          </Box>
        </MuiDrawer>
        <Box
          component='main'
          sx={{
            flexGrow: 1,
            m: 3,
          }}
        >
          <DrawerHeader />
          {renderContent()}
        </Box>
      </Box>
      <OrganizationSwitchModal
        currentOrgId={user?.org_id}
        open={orgSwitchModalOpen}
        onClose={handleCloseOrgSwitchModal}
      />
      <NoTourModal
        open={noTourModalOpen}
        onClose={() => setNoTourModalOpen(false)}
      />
      <UpdateAvailableSnackbar />
    </>
  );
}
